/**
 * TransactionDetails.js
 * ====================
 * Component for displaying detailed transaction information
 * 
 * Features:
 *   - Full transaction details from Memento API
 *   - Action traces and data
 *   - Resource usage statistics
 *   - Authorization details
 *   - Raw JSON view
 *   - Beautiful, organized layout
 * 
 * Author: Block Explorer Team
 * Date: January 2026
 */

import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Panel } from 'primereact/panel';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { Chip } from 'primereact/chip';
import { Button } from 'primereact/button';
import moment from 'moment';
import './TransactionDetails.css';

const TransactionDetails = ({ visible, transaction, onHide, mementoAPI }) => {
    const [txData, setTxData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (visible && transaction) {
            loadTransactionDetails();
        }
    }, [visible, transaction]);

    const loadTransactionDetails = async () => {
        setLoading(true);
        setError(null);

        try {
            // First check if we already have the data in rawData
            if (transaction.rawData && transaction.rawData.data) {
                setTxData(transaction.rawData);
                setLoading(false);
                return;
            }

            // Otherwise, fetch from Memento API
            const response = await fetch(
                `${mementoAPI}/get_transaction?trx_id=${transaction.trx_id}`
            );

            if (!response.ok) {
                throw new Error('Failed to fetch transaction details');
            }

            const data = await response.json();
            setTxData(data);
        } catch (err) {
            console.error('Error loading transaction details:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        
        // Ensure UTC timestamps are treated as UTC
        let timestampStr = dateStr;
        
        // Replace space with 'T' for ISO format and add 'Z' for UTC if missing
        if (typeof timestampStr === 'string') {
            timestampStr = timestampStr.replace(' ', 'T');
            if (!timestampStr.includes('Z') && !timestampStr.includes('+')) {
                timestampStr = timestampStr + 'Z';
            }
        }
        
        // Parse UTC timestamp and convert to local time
        const localTime = moment.utc(timestampStr).local();
        const offset = localTime.format('Z');
        
        return `${localTime.format('MMMM DD, YYYY [at] HH:mm:ss')} (UTC${offset})`;
    };

    const formatTimeAgo = (dateStr) => {
        if (!dateStr) return '';
        
        // Ensure UTC timestamps are treated as UTC
        let timestampStr = dateStr;
        
        // Replace space with 'T' for ISO format and add 'Z' for UTC if missing
        if (typeof timestampStr === 'string') {
            timestampStr = timestampStr.replace(' ', 'T');
            if (!timestampStr.includes('Z') && !timestampStr.includes('+')) {
                timestampStr = timestampStr + 'Z';
            }
        }
        
        // Parse UTC timestamp and convert to local time for relative time
        const localTime = moment.utc(timestampStr).local();
        return localTime.fromNow();
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const renderHeader = () => {
        if (!txData) return null;

        return (
            <div className="tx-dialog-header">
                <div className="tx-header-content">
                    <i className="pi pi-file" style={{ fontSize: '1.5rem', color: '#6366f1' }}></i>
                    <div>
                        <h3>Transaction Details</h3>
                        <p className="tx-subtitle">
                            Block #{txData.data?.block_num?.toLocaleString()} • {formatTimeAgo(txData.data?.block_timestamp)}
                        </p>
                    </div>
                </div>
                <div className="tx-header-status">
                    {txData.irreversible && (
                        <Tag 
                            severity="success" 
                            value="✓ Irreversible"
                            icon="pi pi-check-circle"
                            className="status-tag-large"
                        />
                    )}
                    {txData.data?.trace?.status && (
                        <Tag 
                            severity={txData.data.trace.status === 'executed' ? 'success' : 'danger'} 
                            value={txData.data.trace.status}
                            className="status-tag-large"
                        />
                    )}
                </div>
            </div>
        );
    };

    const renderTransactionInfo = () => {
        if (!txData?.data) return null;

        const data = txData.data;

        return (
            <Panel header="Transaction Information" className="tx-panel">
                <div className="tx-info-grid">
                    <div className="tx-info-row">
                        <div className="tx-info-label">
                            <i className="pi pi-tag"></i>
                            Transaction ID
                        </div>
                        <div className="tx-info-value">
                            <code className="tx-code">{data.trace?.id}</code>
                            <Button
                                icon="pi pi-copy"
                                className="p-button-rounded p-button-text p-button-sm"
                                onClick={() => copyToClipboard(data.trace?.id)}
                                tooltip="Copy to clipboard"
                            />
                        </div>
                    </div>

                    <div className="tx-info-row">
                        <div className="tx-info-label">
                            <i className="pi pi-box"></i>
                            Block Number
                        </div>
                        <div className="tx-info-value">
                            <span className="block-badge">#{data.block_num?.toLocaleString()}</span>
                        </div>
                    </div>

                    <div className="tx-info-row">
                        <div className="tx-info-label">
                            <i className="pi pi-clock"></i>
                            Timestamp
                        </div>
                        <div className="tx-info-value">
                            {formatDateTime(data.block_timestamp)}
                        </div>
                    </div>

                    <div className="tx-info-row">
                        <div className="tx-info-label">
                            <i className="pi pi-bolt"></i>
                            CPU Usage
                        </div>
                        <div className="tx-info-value">
                            <Chip 
                                label={`${data.trace?.cpu_usage_us || 0} μs`} 
                                className="resource-chip cpu-chip"
                            />
                        </div>
                    </div>

                    <div className="tx-info-row">
                        <div className="tx-info-label">
                            <i className="pi pi-database"></i>
                            NET Usage
                        </div>
                        <div className="tx-info-value">
                            <Chip 
                                label={`${data.trace?.net_usage || 0} bytes`} 
                                className="resource-chip net-chip"
                            />
                        </div>
                    </div>

                    {data.trace?.elapsed && (
                        <div className="tx-info-row">
                            <div className="tx-info-label">
                                <i className="pi pi-stopwatch"></i>
                                Elapsed Time
                            </div>
                            <div className="tx-info-value">
                                {data.trace.elapsed} μs
                            </div>
                        </div>
                    )}
                </div>
            </Panel>
        );
    };

    const renderActionTraces = () => {
        if (!txData?.data?.trace?.action_traces) return null;

        const actionTraces = txData.data.trace.action_traces;

        return (
            <Panel header={`Action Traces (${actionTraces.length})`} className="tx-panel">
                {actionTraces.map((trace, index) => (
                    <div key={index} className="action-trace-card">
                        <div className="action-trace-header">
                            <h4>Action #{index + 1}</h4>
                            <Tag 
                                value={trace.receipt?.receiver || 'N/A'} 
                                severity="info"
                            />
                        </div>

                        <div className="action-details-grid">
                            <div className="action-detail-item">
                                <strong>Contract:</strong>
                                <code>{trace.act?.account}</code>
                            </div>

                            <div className="action-detail-item">
                                <strong>Action:</strong>
                                <code className="action-name">{trace.act?.name}</code>
                            </div>

                            {trace.act?.authorization && trace.act.authorization.length > 0 && (
                                <div className="action-detail-item authorization-item">
                                    <strong>Authorization:</strong>
                                    <div className="auth-list">
                                        {trace.act.authorization.map((auth, authIdx) => (
                                            <Chip
                                                key={authIdx}
                                                label={`${auth.actor}@${auth.permission}`}
                                                className="auth-chip"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {trace.receipt && (
                                <>
                                    <div className="action-detail-item">
                                        <strong>Global Sequence:</strong>
                                        <span>{trace.receipt.global_sequence}</span>
                                    </div>

                                    <div className="action-detail-item">
                                        <strong>Receipt Status:</strong>
                                        <Tag 
                                            value={trace.receipt.status} 
                                            severity={trace.receipt.status === 'executed' ? 'success' : 'danger'}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {trace.act?.data && Object.keys(trace.act.data).length > 0 && (
                            <>
                                <Divider />
                                <div className="action-data-section">
                                    <h5>Action Data</h5>
                                    <div className="action-data-grid">
                                        {Object.entries(trace.act.data).map(([key, value]) => (
                                            <div key={key} className="action-data-item">
                                                <strong>{key}:</strong>
                                                <span className="action-data-value">
                                                    {typeof value === 'object' 
                                                        ? JSON.stringify(value, null, 2) 
                                                        : String(value)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {trace.console && (
                            <>
                                <Divider />
                                <div className="console-output">
                                    <h5>Console Output</h5>
                                    <pre>{trace.console}</pre>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </Panel>
        );
    };

    const renderRawJSON = () => {
        if (!txData) return null;

        return (
            <Panel header="Raw JSON Data" className="tx-panel raw-json-panel">
                <div className="json-toolbar">
                    <Button
                        label="Copy JSON"
                        icon="pi pi-copy"
                        className="p-button-sm p-button-outlined"
                        onClick={() => copyToClipboard(JSON.stringify(txData, null, 2))}
                    />
                </div>
                <pre className="tx-json">{JSON.stringify(txData, null, 2)}</pre>
            </Panel>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="tx-loading">
                    <ProgressSpinner />
                    <p>Loading transaction details...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="tx-error">
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#ef4444' }}></i>
                    <h3>Error Loading Transaction</h3>
                    <p>{error}</p>
                </div>
            );
        }

        if (!txData) {
            return (
                <div className="tx-empty">
                    <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
                    <p>No transaction data available</p>
                </div>
            );
        }

        return (
            <div className="tx-details-content">
                {renderTransactionInfo()}
                
                <TabView className="tx-tabs">
                    <TabPanel header="Action Traces" leftIcon="pi pi-list">
                        {renderActionTraces()}
                    </TabPanel>
                    
                    <TabPanel header="Raw JSON" leftIcon="pi pi-code">
                        {renderRawJSON()}
                    </TabPanel>
                </TabView>
            </div>
        );
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={renderHeader()}
            className="tx-details-dialog"
            style={{ width: '90vw', maxWidth: '1200px' }}
            maximizable
            modal
        >
            {renderContent()}
        </Dialog>
    );
};

export default TransactionDetails;
